import { useState, useMemo } from 'react';
import * as Utilities from 'utilities/utils';
import { Box, Stack, Typography } from '@mui/material';
import AIIconButton from 'components/AI/AIIconButton/AIIconButton';
import { useQueryClient } from '@tanstack/react-query';
import { AutoFixHigh } from '@mui/icons-material';
import Select from 'react-select';
import { fileTagLabel } from '@liscio/common/file-tag-label';
import { AlertMessage, isUserEmployee } from 'utilities/utils';
import { filterFileTagOption } from 'utilities/fileTagSelect';
import { AICheckButton } from 'components/AI/AIButton/AICheckButton';
import { useSearchParams } from 'react-router-dom-latest';
import { useTrackEvent } from 'analytics/useTrackEvent';
import { useGetHome } from 'hooks/sms-hooks';
import {
  getGetApiV5FilesPendingReviewQueryKey,
  useGetApiV5FilesIdTagSuggestions,
  usePutApiV5FilesIdUpdateTags,
} from '@liscio/api';
import { mergeTags, TagOptionWithState } from './utils/mergeTags';
import { AITagChip, AITagsSuggestionsLoadingState } from './components';
import { mapTagSuggestions } from './utils';

const MIN_TAG_ROW_HEIGHT = '23px';

export interface AIFileTagsSuggestionsProps {
  fileId: string;
  onChangeTags: (newTags: string[], fileId: string) => void;
  initialTags?: TagOption[];
  isVisible?: boolean;
  aiIngestionStatus?: string;
}

export const AIFileTagsSuggestions = ({
  fileId,
  onChangeTags,
  initialTags = [],
  isVisible = false,
  aiIngestionStatus,
}: AIFileTagsSuggestionsProps) => {
  const [selectedTags, setSelectedTags] = useState<TagOption[]>(initialTags);
  const [searchParams] = useSearchParams();
  const trackEvent = useTrackEvent();
  const { data: homeData } = useGetHome();

  const queryClient = useQueryClient();

  const addTag = (tag: TagOption) => {
    setSelectedTags((prevTags) => [...prevTags, tag]);
  };

  const removeTag = (tag: TagOption) => {
    setSelectedTags((prevTags) =>
      prevTags.filter(
        (selectedTag) =>
          selectedTag.key.toLowerCase() !== tag.key.toLowerCase(),
      ),
    );
  };

  const {
    data: aiTagsSuggestionsResponse,
    isLoading,
    refetch,
    remove,
  } = useGetApiV5FilesIdTagSuggestions(fileId, {
    query: { enabled: isVisible && aiIngestionStatus !== 'failed' },
  });
  const aiTagsSuggestions = mapTagSuggestions(aiTagsSuggestionsResponse);

  const { mutateAsync: changeTags, isLoading: isChangingTags } =
    usePutApiV5FilesIdUpdateTags();

  const handleChangeTags = async () => {
    if (selectedTags && fileId) {
      try {
        const response = await changeTags({
          id: fileId,
          data: { tags: selectedTags.map((tag) => tag.key) },
        });
        const savedTags = response.file.tags;
        setSelectedTags(
          savedTags.map((key) =>
            selectedTags.find(
              (tag) => tag.key.toLowerCase() === key.toLowerCase(),
            ) || { key, label: key.replace(/_/g, ' ') },
          ),
        );
        onChangeTags(savedTags, fileId);

        // Track onboarding analytics if coming from onboarding flow and user is an employee
        const comingFrom = searchParams.get('comingFrom');

        if (comingFrom === 'onboarding' && isUserEmployee()) {
          trackEvent('file_auto_tagged', {
            user_role: 'firm_employee',
            tags_count: savedTags.length,
            company_name: homeData?.data?.userpilot_data?.firm_name,
            onboarding_step: 'file_auto_tagged',
          });
        }
      } catch {
        AlertMessage('error', 'Failed to update tags', 3000);
      }

      // Invalidate the pending review query to refresh the file list
      queryClient.removeQueries({
        queryKey: getGetApiV5FilesPendingReviewQueryKey(),
      });
    }
  };

  const uploadTags = useMemo<TagOption[]>(
    () => Utilities.getStorageData()?.pref_type_options?.upload_tags || [],
    [],
  );

  // Get tags that can't be added/removed
  const tagsCantAddRem = useMemo<TagOption[]>(
    () => {
      const preferences = Utilities.getStorageData()?.pref_type_options;
      return isUserEmployee()
        ? preferences?.tags_emp_cant_addrem || []
        : preferences?.tags_client_cant_addrem || [];
    },
    [],
  );

  const tags = useMemo<TagOptionWithState[]>(
    () =>
      mergeTags(selectedTags, aiTagsSuggestions, uploadTags, tagsCantAddRem),
    [selectedTags, aiTagsSuggestions, uploadTags, tagsCantAddRem],
  );

  const hasAtLeastOneTagSelected =
    tags.filter((tag) => tag.isSelected).length > 0;
  const hasAtLeastOneSuggestedTag =
    tags.filter((tag) => tag.isSuggested).length > 0;

  return (
    <Stack mb={1.25} gap={1.25}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body1" fontWeight={600}>
          Generate AI Suggested Tags
        </Typography>
        <AIIconButton
          customIcon={<AutoFixHigh />}
          size="large"
          onClick={() => {
            remove();
            refetch();
          }}
        />
      </Stack>
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={1.25}
        sx={{ minHeight: MIN_TAG_ROW_HEIGHT }}
      >
        {aiIngestionStatus === 'failed' ? (
          <Typography variant="body1" className="text-muted">
            AI suggestions could not be loaded for this file type
          </Typography>
        ) : (
          <>
            {isLoading && <AITagsSuggestionsLoadingState />}
            {!hasAtLeastOneSuggestedTag && !isLoading && (
              <Typography variant="body1">
                No suggested tags available
              </Typography>
            )}
            {tags.map((tag) => {
              if (tag.isSuggested) {
                return (
                  <AITagChip
                    key={tag.key}
                    label={fileTagLabel(tag.key, tag.label)}
                    isSelected={tag.isSelected}
                    isEditable={tag.isEditable}
                    onDelete={() => {
                      removeTag(tag);
                    }}
                    onSelect={() => {
                      addTag(tag);
                    }}
                  />
                );
              }
              return null;
            })}
          </>
        )}
      </Stack>
      <Stack gap={1.25}>
        <Typography variant="body1" fontWeight={600}>
          Selected Tags ({selectedTags?.length || 0})
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1.25}
          sx={{ paddingRight: '17px' }}
        >
          <Stack
            gap={1.25}
            direction="row"
            flexWrap="wrap"
            sx={{ minHeight: MIN_TAG_ROW_HEIGHT }}
          >
            {tags
              .filter((tag) => tag.isSelected)
              .map((tag) => (
                <AITagChip
                  key={tag.key}
                  label={fileTagLabel(tag.key, tag.label)}
                  isSelected
                  isEditable={tag.isEditable}
                  onDelete={() => {
                    removeTag(tag);
                  }}
                />
              ))}
          </Stack>
          <Box>
            <AICheckButton
              aria-label="Save tags"
              onClick={handleChangeTags}
              disabled={isChangingTags || !hasAtLeastOneTagSelected}
            />
          </Box>
        </Stack>
      </Stack>
      <Stack gap={1.25}>
        <Typography variant="body1" fontWeight={600}>
          Add Tag from Library
        </Typography>
        <Select
          isDisabled={isChangingTags}
          placeholder="Search and add tags..."
          options={tags.filter((tag) => !tag.isSelected && tag.isEditable)}
          formatOptionLabel={(tag) =>
            tag ? fileTagLabel(tag.key, tag.label) : ''
          }
          filterOption={filterFileTagOption}
          onChange={(selectedOption) => {
            if (selectedOption) {
              addTag(selectedOption);
            }
          }}
          value={null}
          menuPlacement="top"
        />
      </Stack>
    </Stack>
  );
};
